'use client'

import React, { useCallback, useState } from 'react'
import { cn } from '../../../lib/utils'
import Icon from '../Icon'
import {
  DropdownMenuComponent as DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../DropDownMenu'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '../Drawer'
import { useMediaQuery } from '../Threads/hooks/useMediaQuery'
import {
  PageTypeaheadPanel,
  type PageTypeaheadResult,
} from '../PageTypeaheadPopover'
import type { SidebarNavigationMeta } from '../SideNavigationV2/SideBar/SidebarNavigationDrag'
import {
  useSidebarNavigationEdit,
  WorkspaceNavItemEditForm,
  SidebarNavItemEditTrigger,
} from '../WorkspaceNavItemEditDrawer'

export type WorkspaceNavItemMenuProps = {
  item: SidebarNavigationMeta
  label: string
  icon?: string | null
  canDelete?: boolean
  showIconEditor?: boolean
  triggerClassName?: string
}

type ItemMenuPanel = 'menu' | 'page' | 'edit'

const menuOptionIconClassName =
  'flex size-3.5 shrink-0 items-center justify-center'

export function SidebarNavItemMenuControl({
  item,
  label,
  icon,
  canDelete = true,
  showIconEditor = true,
  triggerClassName,
}: WorkspaceNavItemMenuProps) {
  const { enabled, onUpdate, onDelete, itemMenu } = useSidebarNavigationEdit()
  const isMobile = useMediaQuery('(max-width: 767px)')
  const [open, setOpen] = useState(false)
  const [panel, setPanel] = useState<ItemMenuPanel>('menu')

  const showAddPage = Boolean(itemMenu?.pageTypeahead)
  const navItem = { ...item, label, icon: icon ?? item.icon ?? undefined }

  const resetMenu = useCallback(() => {
    setOpen(false)
    setPanel('menu')
  }, [])

  const handleOpenChange = useCallback((nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setPanel('menu')
    }
  }, [])

  const handleRemove = useCallback(async () => {
    resetMenu()
    await onDelete(item.id)
  }, [item.id, onDelete, resetMenu])

  const handleAddPageSelect = useCallback(
    async (page: PageTypeaheadResult) => {
      if (itemMenu?.onAddPageUnderItem) {
        await itemMenu.onAddPageUnderItem(item.id, page)
      } else {
        await itemMenu?.pageTypeahead?.onSelect(page)
      }
      resetMenu()
    },
    [item.id, itemMenu, resetMenu],
  )

  if (!enabled) return null

  const trigger = <SidebarNavItemEditTrigger className={triggerClassName} />

  const panelTitle =
    panel === 'page'
      ? itemMenu?.pageTypeahead?.title ?? 'Add page'
      : panel === 'edit'
        ? 'Edit'
        : 'Navigation item'

  const renderPanelContent = () => {
    if (panel === 'page' && itemMenu?.pageTypeahead) {
      return (
        <PageTypeaheadPanel
          placeholder={itemMenu.pageTypeahead.placeholder}
          emptyMessage={itemMenu.pageTypeahead.emptyMessage}
          onSearch={(query) =>
            itemMenu.onSearchPagesUnderItem
              ? itemMenu.onSearchPagesUnderItem(item.id, query)
              : itemMenu.pageTypeahead!.onSearch(query)
          }
          onSelect={handleAddPageSelect}
          onCreateNewPage={itemMenu.pageTypeahead.onCreateNewPage}
          createNewPageLabel={itemMenu.pageTypeahead.createNewPageLabel}
          onClose={resetMenu}
        />
      )
    }

    if (panel === 'edit') {
      return (
        <WorkspaceNavItemEditForm
          item={navItem}
          open
          canDelete={canDelete}
          showIconEditor={showIconEditor}
          onSave={onUpdate}
          onDelete={onDelete}
          onClose={resetMenu}
        />
      )
    }

    return null
  }

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={handleOpenChange}>
        <DrawerTrigger asChild>{trigger}</DrawerTrigger>
        <DrawerContent className="max-h-[90vh] flex flex-col px-0">
          <DrawerHeader className="text-left pb-2 px-4">
            <div className="flex items-center gap-2">
              {panel !== 'menu' ? (
                <button
                  type="button"
                  aria-label="Back"
                  className="rounded-md p-1 hover:bg-accent/50 text-muted-foreground/80"
                  onClick={() => setPanel('menu')}
                >
                  <Icon name="ChevronLeft" size={16} />
                </button>
              ) : null}
              <DrawerTitle className="text-sm">{panelTitle}</DrawerTitle>
            </div>
          </DrawerHeader>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
            {panel === 'menu' ? (
              <div className="flex flex-col gap-1">
                <button
                  type="button"
                  className="flex h-9 items-center gap-2 rounded-sm px-2 text-left text-xs"
                  onClick={() => setPanel('edit')}
                >
                  <Icon name="Pencil" size={14} />
                  Edit
                </button>
                {showAddPage ? (
                  <button
                    type="button"
                    className="flex h-9 items-center gap-2 rounded-sm px-2 text-left text-xs"
                    onClick={() => setPanel('page')}
                  >
                    <Icon name="FileText" size={14} />
                    Add page
                  </button>
                ) : null}
                {canDelete ? (
                  <button
                    type="button"
                    className="flex h-9 items-center gap-2 rounded-sm px-2 text-left text-xs text-destructive"
                    onClick={() => void handleRemove()}
                  >
                    <Icon name="Trash2" size={14} />
                    Remove
                  </button>
                ) : null}
              </div>
            ) : (
              renderPanelContent()
            )}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <DropdownMenu modal={false} open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className={cn(panel === 'menu' ? 'min-w-[160px] p-1' : 'w-72 p-2')}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
        }}
      >
        {panel !== 'menu' ? (
          <div className="px-2 pb-1 text-[11px] font-medium text-muted-foreground/80">
            {panelTitle}
          </div>
        ) : null}

        {panel === 'menu' ? (
          <>
            <DropdownMenuItem
              className="gap-2 px-2 text-xs"
              onSelect={(event) => {
                event.preventDefault()
                setPanel('edit')
              }}
            >
              <span className={menuOptionIconClassName}>
                <Icon name="Pencil" size={14} />
              </span>
              Edit
            </DropdownMenuItem>
            {showAddPage ? (
              <DropdownMenuItem
                className="gap-2 px-2 text-xs"
                onSelect={(event) => {
                  event.preventDefault()
                  setPanel('page')
                }}
              >
                <span className={menuOptionIconClassName}>
                  <Icon name="FileText" size={14} />
                </span>
                Add page
              </DropdownMenuItem>
            ) : null}
            {canDelete ? (
              <DropdownMenuItem
                className="gap-2 px-2 text-xs text-destructive focus:text-destructive"
                onSelect={(event) => {
                  event.preventDefault()
                  void handleRemove()
                }}
              >
                <span className={menuOptionIconClassName}>
                  <Icon name="Trash2" size={14} />
                </span>
                Remove
              </DropdownMenuItem>
            ) : null}
          </>
        ) : (
          renderPanelContent()
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
